import { Dialog, IconButton, Divider, Paper, Button, Tab } from '@mui/material'
import { Box, styled } from '@mui/system'
import CloseIcon from '@mui/icons-material/Close'
import { useEffect, useState } from 'react'
import { toastFailure } from '../../../util/toast'
import CTRLoader from '../../ui/loader/CTRloader'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import ProductTab from './ProductTab'
import { AssemblyRelation, getAssemblyByIdRequest } from '../../../api/assemblies'
import AssemblyTab from './AssemblyTab'
import StatusTab from './StatusTab'
import CallOffTab from './CallOffTab'
import { Assembly } from '../../../models/models'

const Container = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
})

const Content = styled(Box)({
    width: '100%',
})

const FooterContainer = styled(Box)(() => ({
    display: 'flex',
    width: '95%',
    justifyContent: 'space-between',
    margin: '10px 20px 10px 20px',
}))

const FooterButton = styled(Button)(() => ({
    margin: '10px',
}))

interface InspectAssemblyModalProps {
    assemblyId: number
    closeModal: () => void
    onOk?: () => void
    isOpen: boolean
}

enum State {
    Loading = 'Loading',
    Found = 'Found',
    NotFound = 'NotFound',
    Error = 'Error',
}

export type Tabs = 'CallOff' | 'Assembly' | 'Product' | 'Status'

export default function InspectAssemblyModal(props: InspectAssemblyModalProps) {
    const { assemblyId, closeModal, isOpen, onOk } = props
    const [activeTab, setActiveTab] = useState<Tabs>('Assembly')
    const handleTabChange = (event: React.SyntheticEvent, tabKey: Tabs) => {
        event.preventDefault()
        setActiveTab(tabKey)
    }
    const [assembly, setAssembly] = useState<Assembly>()
    const [state, setState] = useState<State>(State.Loading)

    const getAssembly = async () => {
        const response = await getAssemblyByIdRequest(assemblyId, {
            relations: [
                AssemblyRelation.Bom,
                AssemblyRelation.CallOff,
                AssemblyRelation.Assemblers,
                AssemblyRelation.CallOffOrders,
                AssemblyRelation.CallOffProject,
            ],
        })

        if (response.successful) {
            setAssembly(response.data)
            setState(State.Found)
        } else if (response.status === 404) {
            setState(State.NotFound)
            toastFailure('Could not find the assembly worksheet')
        } else {
            setState(State.Error)
            toastFailure('Something went wrong retrieving the call off')
        }
    }

    useEffect(() => {
        getAssembly()
    }, [])

    return (
        <Dialog open={isOpen} onClose={closeModal} fullWidth maxWidth="md">
            <Paper style={{ height: '100%', maxHeight: '800px' }}>
                <Box
                    sx={{
                        fontSize: 16,
                        fontWeight: 'bold',
                        padding: '15px 15px 10px 23px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div> Assembly </div>
                    <IconButton aria-label="close" onClick={closeModal}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />
                <Container>
                    {state === State.Loading && (
                        <Content
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '20px',
                                margin: '20px',
                                width: '100%',
                                height: '400px',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <CTRLoader size="xl" />
                        </Content>
                    )}
                    {assembly && state === State.Found && (
                        <Content>
                            <TabContext value={activeTab}>
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        padding: '20px 50px 0px 50px',
                                    }}
                                >
                                    <TabList onChange={handleTabChange}>
                                        <Tab
                                            label="Assembly"
                                            value="Assembly"
                                            style={{ textTransform: 'none' }}
                                        />
                                        <Tab
                                            label="Product"
                                            value="Product"
                                            style={{ textTransform: 'none' }}
                                        />
                                        <Tab
                                            label="Status"
                                            value="Status"
                                            style={{ textTransform: 'none' }}
                                        />
                                        <Tab
                                            label="Call Off"
                                            value="CallOff"
                                            style={{ textTransform: 'none' }}
                                        />
                                    </TabList>
                                </Box>
                                <TabPanel value="Assembly">
                                    <AssemblyTab assembly={assembly} />
                                </TabPanel>
                                <TabPanel value="CallOff">
                                    <CallOffTab assembly={assembly} />
                                </TabPanel>
                                <TabPanel value="Product">
                                    <ProductTab assembly={assembly} />
                                </TabPanel>
                                <TabPanel value="Status">
                                    <StatusTab assembly={assembly} />
                                </TabPanel>
                            </TabContext>
                        </Content>
                    )}
                </Container>
                <FooterContainer>
                    <Box>
                        <FooterButton variant="outlined" onClick={closeModal}>
                            Back
                        </FooterButton>
                        {onOk && (
                            <FooterButton variant="outlined" onClick={() => onOk()}>
                                Done
                            </FooterButton>
                        )}
                    </Box>
                </FooterContainer>
            </Paper>
        </Dialog>
    )
}
